import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, Car } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientVehiclesModule({ authHeaders }) {
  const [vehicles, setVehicles] = useState([]);
  const [clients, setClients] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const emptyForm = { client_id: '', brand: '', model: '', plate: '', vin: '', year: new Date().getFullYear(), mileage: 0, fuel: 'Essence', transmission: 'Manuelle', color: '', observations: '' };
  const [form, setForm] = useState(emptyForm);

  const fetch = useCallback(async () => {
    const [vRes, cRes] = await Promise.all([
      axios.get(`${API_URL}/api/crm/client-vehicles`, { headers: authHeaders }),
      axios.get(`${API_URL}/api/crm/clients`, { headers: authHeaders })
    ]);
    setVehicles(vRes.data);
    setClients(cRes.data);
  }, [authHeaders]);

  useEffect(() => { fetch(); }, [fetch]);

  const clientName = (cid) => { const c = clients.find(cl => cl.id === cid); return c ? `${c.first_name} ${c.last_name}`.trim() : 'Inconnu'; };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editing) await axios.put(`${API_URL}/api/crm/client-vehicles/${editing.id}`, form, { headers: authHeaders });
      else await axios.post(`${API_URL}/api/crm/client-vehicles`, form, { headers: authHeaders });
      setShowForm(false); setEditing(null); fetch();
    } catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (id) => { if (window.confirm('Supprimer ?')) { await axios.delete(`${API_URL}/api/crm/client-vehicles/${id}`, { headers: authHeaders }); fetch(); }};

  return (
    <div data-testid="client-vehicles-module">
      <div className="flex justify-between items-center mb-6">
        <p className="text-sm text-gray-500">{vehicles.length} véhicule{vehicles.length > 1 ? 's' : ''}</p>
        <button onClick={() => { setForm(emptyForm); setEditing(null); setShowForm(true); }} className="flex items-center gap-2 bg-[#DC2626] text-white px-4 py-2 rounded-lg text-sm hover:bg-red-700" data-testid="add-client-vehicle-btn"><Plus size={16} /> Ajouter</button>
      </div>

      <div className="bg-white rounded-xl border">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50"><tr><th className="text-left p-3">Véhicule</th><th className="text-left p-3 hidden md:table-cell">Immatriculation</th><th className="text-left p-3">Client</th><th className="text-left p-3 hidden lg:table-cell">Km</th><th className="text-right p-3">Actions</th></tr></thead>
            <tbody>
              {vehicles.map(v => (
                <tr key={v.id} className="border-t hover:bg-gray-50">
                  <td className="p-3 font-medium">{v.brand} {v.model} <span className="text-gray-400">{v.year}</span></td>
                  <td className="p-3 hidden md:table-cell font-mono text-gray-600">{v.plate}</td>
                  <td className="p-3 text-gray-600">{clientName(v.client_id)}</td>
                  <td className="p-3 hidden lg:table-cell text-gray-600">{v.mileage?.toLocaleString('fr-FR')} km</td>
                  <td className="p-3 text-right">
                    <button onClick={() => { setForm(v); setEditing(v); setShowForm(true); }} className="p-1.5 hover:bg-amber-50 rounded text-amber-600"><Pencil size={16} /></button>
                    <button onClick={() => handleDelete(v.id)} className="p-1.5 hover:bg-red-50 rounded text-red-600"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
              {!vehicles.length && <tr><td colSpan={5} className="p-8 text-center text-gray-400">Aucun véhicule client</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b"><h3 className="font-semibold">{editing ? 'Modifier' : 'Nouveau véhicule client'}</h3></div>
            <form onSubmit={handleSave} className="p-5 space-y-3">
              <select value={form.client_id} onChange={e => setForm({...form, client_id: e.target.value})} required className="w-full border rounded-lg px-3 py-2 text-sm">
                <option value="">Sélectionner un client *</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name} {c.company_name}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="Marque *" required value={form.brand} onChange={e => setForm({...form, brand: e.target.value})} className="border rounded-lg px-3 py-2 text-sm" />
                <input placeholder="Modèle" value={form.model} onChange={e => setForm({...form, model: e.target.value})} className="border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="Immatriculation" value={form.plate} onChange={e => setForm({...form, plate: e.target.value})} className="border rounded-lg px-3 py-2 text-sm" />
                <input placeholder="VIN" value={form.vin} onChange={e => setForm({...form, vin: e.target.value})} className="border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <input type="number" placeholder="Année" value={form.year} onChange={e => setForm({...form, year: parseInt(e.target.value) || 0})} className="border rounded-lg px-3 py-2 text-sm" />
                <input type="number" placeholder="Km" value={form.mileage} onChange={e => setForm({...form, mileage: parseInt(e.target.value) || 0})} className="border rounded-lg px-3 py-2 text-sm" />
                <input placeholder="Couleur" value={form.color} onChange={e => setForm({...form, color: e.target.value})} className="border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <select value={form.fuel} onChange={e => setForm({...form, fuel: e.target.value})} className="border rounded-lg px-3 py-2 text-sm">
                  {['Essence','Diesel','Hybride','Électrique','GPL'].map(f => <option key={f}>{f}</option>)}
                </select>
                <select value={form.transmission} onChange={e => setForm({...form, transmission: e.target.value})} className="border rounded-lg px-3 py-2 text-sm">
                  <option>Manuelle</option><option>Automatique</option>
                </select>
              </div>
              <textarea placeholder="Observations" value={form.observations} onChange={e => setForm({...form, observations: e.target.value})} rows={2} className="w-full border rounded-lg px-3 py-2 text-sm" />
              <div className="flex justify-end gap-3 pt-3">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Annuler</button>
                <button type="submit" className="px-4 py-2 text-sm bg-[#DC2626] text-white rounded-lg hover:bg-red-700">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
